/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { parentLayerOf } from './facts/resolver';
import {
  ADSERVER_KV_FACT_PREFIX,
  CompoundRule,
  PAGE_KVS_HARVESTED_FACT,
  Rule,
  RuleOperator,
} from './rule';

import type {
  CompoundCondition,
  Condition,
  Conditions,
  ConditionType,
  FailedCondition,
  Facts,
  InputConditions,
} from './index';
import type { RuleNode } from './rule';

/**
 * Bitmask of the fact scopes a compiled rule set references, ordered
 * narrowest entity scope first. A set whose mask excludes a scope is
 * guaranteed not to read facts of that scope, which is what makes
 * cross-entity result sharing safe. UNKNOWN marks a fact whose prefix is
 * neither an entity scope nor a recognised page-scope prefix; it always
 * blocks sharing, so an unforeseen fact key can never be treated as page
 * constant.
 */
export const FactScope = {
  AD: 1,
  CAMPAIGN: 2,
  PLACEMENT: 4,
  UNKNOWN: 8,
} as const;

// Facts held by the page-wide store: constant across placements, campaigns and ads
const PAGE_FACT_PREFIXES = ['page.', 'browser.', 'device.', 'geo.', 'time.', 'publisher.'];

/**
 * A parsed rule tree shared by every entity whose conditions JSON is
 * identical. Compiled once per distinct conditions value per page. The rule
 * tree is immutable; shareKey/sharedResult are a single-slot result cache
 * (see shareKeyFor) letting entities that would ask this set the same
 * question reuse one evaluation.
 */
export interface CompiledRuleSet {
  conditions: Conditions;
  scopes: number;
  isEmpty: boolean;
  shareKey?: Facts;
  sharedResult?: boolean;
}

const ruleSetCache = new Map<string, CompiledRuleSet>();

const isCompoundCondition = (rule: Condition | CompoundCondition): rule is CompoundCondition => {
  return 'op' in rule && 'rules' in rule && !('fact' in rule) && !('val' in rule);
};

const convertToRuleNode = (rule: Condition | CompoundCondition): RuleNode => {
  if (isCompoundCondition(rule)) {
    return new CompoundRule(
      rule.op,
      rule.rules.map((childRule) => convertToRuleNode(childRule)),
    );
  }
  return new Rule(rule.fact, rule.op, rule.val);
};

const scopeOfFact = (fact: string): number => {
  if (fact.startsWith('ad.')) return FactScope.AD;
  if (fact.startsWith('campaign.')) return FactScope.CAMPAIGN;
  if (fact.startsWith('placement.')) return FactScope.PLACEMENT;
  for (let i = 0; i < PAGE_FACT_PREFIXES.length; i++) {
    if (fact.startsWith(PAGE_FACT_PREFIXES[i])) {
      return 0;
    }
  }
  return FactScope.UNKNOWN;
};

const collectScopes = (node: RuleNode): number => {
  if (node instanceof Rule) {
    return scopeOfFact(node.fact);
  }
  let scopes = 0;
  for (const child of node.rules) {
    scopes |= collectScopes(child);
  }
  return scopes;
};

// Fail-fast ordering weight: plain comparisons first, frequency-window
// expressions (timestamp filtering) next, compounds last
const evaluationCost = (node: RuleNode): number => {
  if (node instanceof CompoundRule) {
    return 2;
  }
  if (
    (node.operator === RuleOperator.LTE || node.operator === RuleOperator.GTE) &&
    typeof node.value === 'string'
  ) {
    return 1;
  }
  return 0;
};

/**
 * Parses raw conditions into a CompiledRuleSet. Accepts already-constructed
 * Rule/CompoundRule instances alongside JSON conditions, mirroring the
 * historical Engine input contract.
 */
export const compileConditions = (input: InputConditions): CompiledRuleSet => {
  const conditions: Conditions = {};
  let scopes = 0;
  let isEmpty = true;
  for (const key in input) {
    if (input.hasOwnProperty(key)) {
      const conditionKey = key as keyof InputConditions;
      const nodes = input[conditionKey]!.map((rule) => {
        if (rule instanceof Rule || rule instanceof CompoundRule) {
          return rule;
        }
        return convertToRuleNode(rule);
      });
      // ALL short-circuits on the first failure, so evaluate cheap rules
      // first; ANY/NONE keep authored order (the ANY collector reports the
      // whole section, and reordering would change its entry order)
      if (conditionKey === 'ALL' && nodes.length > 1) {
        nodes.sort((a, b) => evaluationCost(a) - evaluationCost(b));
      }
      conditions[conditionKey] = nodes;
      if (nodes.length > 0) {
        isEmpty = false;
        for (const node of nodes) {
          scopes |= collectScopes(node);
        }
      }
    }
  }
  return { conditions, scopes, isEmpty };
};

/**
 * Returns the shared CompiledRuleSet for the given conditions, compiling on
 * first sight. Only for plain JSON conditions (the delivery config): the
 * cache key is the JSON serialisation, so Rule/CompoundRule instances must go
 * through compileConditions directly. The cache is page-lifetime and bounded
 * by the number of distinct rule sets in the config.
 */
export const internConditions = (input: InputConditions): CompiledRuleSet => {
  const key = JSON.stringify(input);
  let set = ruleSetCache.get(key);
  if (!set) {
    set = compileConditions(input);
    ruleSetCache.set(key, set);
  }
  return set;
};

export const _resetRuleSetCacheForTests = (): void => {
  ruleSetCache.clear();
};

/**
 * The fact layer identifying the answer this set would give, or null when the
 * result cannot be shared beyond the calling entity.
 *
 * A set's verdict varies only with the facts it reads. Since each layer
 * carries only its own scope's facts, a set referencing nothing at or below
 * the caller's own scope gives the same answer for every entity descending
 * from the ancestor layer holding the narrowest scope it does reference — so
 * that layer identifies the answer. Sets reading the caller's own scope (or
 * any unrecognised fact) are not shareable.
 *
 * `ownScope` is the caller's FactScope; `parentLayer` is the layer it
 * evaluates against, one level up.
 */
export const shareKeyFor = (
  set: CompiledRuleSet,
  ownScope: number,
  parentLayer: Facts,
): Facts | null => {
  // Every bit at or below ownScope, plus UNKNOWN
  const blocking = FactScope.UNKNOWN | (ownScope * 2 - 1);
  if (set.scopes & blocking) {
    return null;
  }
  let key: Facts | null = parentLayer;
  // Climb past each scope the set does not read; a set reading none reaches the page layer
  for (let scope = ownScope * 2; scope < FactScope.UNKNOWN && !(set.scopes & scope); scope *= 2) {
    key = key === null ? null : parentLayerOf(key);
    if (key === null || key === Object.prototype) {
      return null;
    }
  }
  return key;
};

// A page-KV key absent from a successfully harvested page targeting set is
// absent from the ad request too, so the ad server line could never match it
const failsClosedOnAbsence = (rule: RuleNode, facts: Facts): boolean =>
  rule instanceof Rule &&
  rule.fact.startsWith(ADSERVER_KV_FACT_PREFIX) &&
  facts[PAGE_KVS_HARVESTED_FACT] !== undefined;

const evaluateRuleNode = (rule: RuleNode, facts: Facts): boolean => {
  if (rule instanceof Rule) {
    return rule.evaluate(facts[rule.fact]);
  } else if (rule instanceof CompoundRule) {
    return rule.evaluate(facts);
  }
  throw new Error('Invalid rule type');
};

// Compact one-line rendering of a compound, e.g. 'AND(a,OR(b,c))'
const describeRuleNode = (rule: RuleNode): string => {
  if (rule instanceof Rule) {
    return rule.fact;
  }
  return `${rule.op}(${rule.rules.map((child) => describeRuleNode(child)).join(',')})`;
};

// Builds the FailedCondition entry for a rule that contributed to a failing run
const describeFailure = (cond: ConditionType, rule: RuleNode, facts: Facts): FailedCondition => {
  // 'S' lumps fact-absent with unknown-operator; ANY entries and fail-closed
  // absent page-KV ALL entries carry it
  const st = rule.isEvaluable(facts) ? 'F' : 'S';
  if (rule instanceof Rule) {
    return st === 'F'
      ? { cond, fact: rule.fact, op: rule.operator, st, actual: facts[rule.fact] }
      : { cond, fact: rule.fact, op: rule.operator, st };
  }
  return { cond, fact: describeRuleNode(rule), op: rule.op, st };
};

/**
 * Evaluates a compiled rule set against the facts. Fail-fast: each section
 * short-circuits on the condition that decides its verdict, and the first
 * failing section ends the run.
 *
 * Conditions on absent facts are skipped (fail open) in every section: rules
 * replicate ad server line targeting, and a fact can be legitimately
 * unavailable (older tag version, app traffic, a KV the page does not
 * expose). The ad server still enforces its own targeting, so a skipped
 * condition costs at most a wasted candidate, whereas failing closed silently
 * blocks delivery. ANY: at least one evaluable condition must pass; with no
 * evaluable conditions the section passes. ALL: every evaluable condition
 * must pass. NONE: an eligible candidate must match no evaluable condition.
 *
 * Exception: an ANY/ALL condition on an absent `page.kvs.*` fact fails
 * (rather than skips) once PAGE_KVS_HARVESTED_FACT is present. The page's
 * ad server targeting was read and the key is not there, so the ad server
 * line could never match it either and the candidate is undeliverable - the
 * live case being Schibsted consent-or-pay subscribers whose glimr keys are
 * omitted entirely. NONE keeps skip semantics (a missing key cannot match an
 * exclusion), and compound conditions keep their own internal semantics.
 *
 * When `collector` is provided, the condition(s) that caused a failing run
 * are appended to it: the first failing ALL rule, the first matching NONE
 * rule, or — for a failed ANY section, whose verdict is inherently
 * collective — one entry per section rule. Evaluation order and
 * short-circuiting are identical with or without a collector; a passing run
 * never touches it.
 */
export const evaluateCompiled = (
  set: CompiledRuleSet,
  facts: Facts,
  collector?: FailedCondition[],
): boolean => {
  const conditions = set.conditions;
  if (conditions.ANY && conditions.ANY.length > 0) {
    let sawEvaluable = false;
    let sawPass = false;
    for (const rule of conditions.ANY) {
      if (!rule.isEvaluable(facts)) {
        if (failsClosedOnAbsence(rule, facts)) {
          sawEvaluable = true;
        }
        continue;
      }
      sawEvaluable = true;
      if (evaluateRuleNode(rule, facts)) {
        sawPass = true;
        break;
      }
    }
    if (sawEvaluable && !sawPass) {
      if (collector) {
        // The loop above already visited every rule; this re-walk evaluates nothing new
        for (const rule of conditions.ANY) {
          collector.push(describeFailure('ANY', rule, facts));
        }
      }
      return false;
    }
  }

  if (conditions.ALL && conditions.ALL.length > 0) {
    for (const rule of conditions.ALL) {
      const failed = rule.isEvaluable(facts)
        ? !evaluateRuleNode(rule, facts)
        : failsClosedOnAbsence(rule, facts);
      if (failed) {
        if (collector) {
          collector.push(describeFailure('ALL', rule, facts));
        }
        return false;
      }
    }
  }

  if (conditions.NONE && conditions.NONE.length > 0) {
    for (const rule of conditions.NONE) {
      if (rule.isEvaluable(facts) && evaluateRuleNode(rule, facts)) {
        if (collector) {
          collector.push(describeFailure('NONE', rule, facts));
        }
        return false;
      }
    }
  }

  return true;
};
